import { ReportKind, triggerReportMail } from "@rpforms/shared";
import { default as React, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import styled from "styled-components";

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`;

const Label = styled.span`
    font-size: 14px;
    color: #6c757d;
    margin-right: 4px;
`;

const MONTH_NAMES = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
];

const FIRST_YEAR = 2019;

export const ReportMailButtons = () => {
    const [pending, setPending] = useState<ReportKind | null>(null);
    const [maintenanceMonth, setMaintenanceMonth] = useState("");
    const [maintenanceYear, setMaintenanceYear] = useState("");

    const now = new Date();
    const years = Array.from({ length: now.getFullYear() - FIRST_YEAR + 1 }, (_, i) =>
        String(now.getFullYear() - i)
    );
    const isFutureMonth = (monthIndex: number) =>
        Number(maintenanceYear) === now.getFullYear() && monthIndex > now.getMonth();
    const chosenMonth =
        maintenanceMonth && maintenanceYear
            ? `${maintenanceYear}-${maintenanceMonth.padStart(2, "0")}`
            : undefined;

    const reports: Array<{ kind: ReportKind; label: string; month?: string }> = [
        { kind: "open_invoices", label: "Offene Rechnungen" },
        {
            kind: "maintenance_invoices",
            label: chosenMonth
                ? `Wartungen ${MONTH_NAMES[Number(maintenanceMonth) - 1]} ${maintenanceYear}`
                : "Wartungen Vorjahr",
            month: chosenMonth,
        },
    ];

    const send = async (kind: ReportKind, month?: string) => {
        setPending(kind);
        try {
            const result = await triggerReportMail(kind, { month });
            toast.success(`Report wird versendet an ${result.recipients.join(", ")}`);
        } catch (e) {
            const status = e?.response?.status;
            toast.error(
                status === 401
                    ? "Keine Berechtigung zum Versenden der Reports"
                    : status === 422
                    ? "Ungültiger Monat"
                    : "Report konnte nicht versendet werden"
            );
        } finally {
            setPending(null);
        }
    };

    const selectYear = (year: string) => {
        setMaintenanceYear(year);
        if (Number(year) === now.getFullYear() && Number(maintenanceMonth) - 1 > now.getMonth()) {
            setMaintenanceMonth("");
        }
    };

    return (
        <Wrapper>
            <Label>Report-Mail jetzt senden:</Label>
            {reports.map(({ kind, label, month }) => (
                <React.Fragment key={kind}>
                    {kind === "maintenance_invoices" && (
                        <>
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: "auto" }}
                                value={maintenanceMonth}
                                onChange={(e) => setMaintenanceMonth(e.target.value)}
                            >
                                <option value="">Monat</option>
                                {MONTH_NAMES.map((name, i) => (
                                    <option
                                        key={name}
                                        value={String(i + 1)}
                                        disabled={isFutureMonth(i)}
                                    >
                                        {name}
                                    </option>
                                ))}
                            </Form.Control>
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: "auto" }}
                                value={maintenanceYear}
                                onChange={(e) => selectYear(e.target.value)}
                            >
                                <option value="">Jahr</option>
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </Form.Control>
                        </>
                    )}
                    <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={pending !== null}
                        onClick={() => send(kind, month)}
                    >
                        {pending === kind ? (
                            <Spinner animation="border" size="sm" style={{ marginRight: 6 }} />
                        ) : null}
                        {label}
                    </Button>
                </React.Fragment>
            ))}
        </Wrapper>
    );
};
